import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import api from "../api/axios";
import toast from "react-hot-toast";

export default function AdminUsersPage() {
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingUserId, setTogglingUserId] = useState("");

  useEffect(() => {
    const run = async () => {
      try {
        const { data } = await api.get("/admin/users");
        setUsers(Array.isArray(data) ? data : []);
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load users");
      } finally {
        setLoading(false);
      }
    };

    run();
  }, []);

  const filtered = useMemo(() => {
    return users.filter((u) =>
      `${u.fullName || ""} ${u.email || ""}`.toLowerCase().includes(search.toLowerCase())
    );
  }, [users, search]);

  const toggleUser = async (id) => {
    if (togglingUserId) return;
    setTogglingUserId(id);
    try {
      const { data } = await api.patch(`/admin/users/${id}/toggle`);
      toast.success(data.message || "User status updated");
      setUsers((prev) => prev.map((u) => (u._id === id ? { ...u, isActive: !u.isActive } : u)));
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update user");
    } finally {
      setTogglingUserId("");
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 text-white">
      <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-sm uppercase tracking-[0.24em] text-cyan-300/80">Admin</p>
            <h1 className="mt-2 text-3xl font-bold text-white">User Management</h1>
            <p className="mt-2 text-sm text-slate-400">
              Review accounts, search users quickly, and manage account access.
            </p>
          </div>
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-3 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-xl border border-white/15 bg-white/5 py-2.5 pl-10 pr-4 text-white placeholder:text-slate-400 outline-none transition focus:border-cyan-300/40 focus:ring-2 focus:ring-cyan-300/20"
            />
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-[1.75rem] border border-white/10 bg-slate-900/75 p-5 backdrop-blur-xl">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Total Users</p>
          <p className="mt-3 text-3xl font-bold text-white">{users.length}</p>
        </div>
        <div className="rounded-[1.75rem] border border-white/10 bg-slate-900/75 p-5 backdrop-blur-xl">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Active</p>
          <p className="mt-3 text-3xl font-bold text-emerald-300">
            {users.filter((user) => user.isActive).length}
          </p>
        </div>
        <div className="rounded-[1.75rem] border border-white/10 bg-slate-900/75 p-5 backdrop-blur-xl">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Suspended</p>
          <p className="mt-3 text-3xl font-bold text-rose-300">
            {users.filter((user) => !user.isActive).length}
          </p>
        </div>
      </section>

      <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-4 md:p-6 backdrop-blur-xl">
        {loading && <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-slate-300">Loading users...</div>}

        {!loading && filtered.length === 0 && (
          <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center text-slate-400">
            No users found.
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="min-w-full border-separate border-spacing-y-2 text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-400">
                    <th className="px-4 py-2 font-semibold">Name</th>
                    <th className="px-4 py-2 font-semibold">Email</th>
                    <th className="px-4 py-2 font-semibold">Role</th>
                    <th className="px-4 py-2 font-semibold">Status</th>
                    <th className="px-4 py-2 font-semibold">Joined</th>
                    <th className="px-4 py-2 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((user) => (
                    <tr key={user._id} className="bg-white/5 text-slate-200">
                      <td className="rounded-l-2xl px-4 py-4 font-medium text-white">{user.fullName}</td>
                      <td className="px-4 py-4 text-slate-300">{user.email}</td>
                      <td className="px-4 py-4 capitalize text-slate-300">{user.role || "user"}</td>
                      <td className="px-4 py-4">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                            user.isActive
                              ? "bg-emerald-500/15 text-emerald-300"
                              : "bg-rose-500/15 text-rose-300"
                          }`}
                        >
                          {user.isActive ? "Active" : "Suspended"}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-slate-400">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                      <td className="rounded-r-2xl px-4 py-4 text-right">
                        <button
                          onClick={() => toggleUser(user._id)}
                          disabled={Boolean(togglingUserId)}
                          aria-busy={togglingUserId === user._id}
                          className={`rounded-xl px-3 py-2 text-xs font-semibold transition ${
                            user.isActive
                              ? "bg-rose-500/15 text-rose-300 hover:bg-rose-500/25"
                              : "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"
                          }`}
                        >
                          {togglingUserId === user._id ? "Updating…" : user.isActive ? "Suspend" : "Activate"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="space-y-3 md:hidden">
              {filtered.map((user) => (
                <div key={user._id} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-base font-semibold text-white">{user.fullName}</p>
                      <p className="text-sm text-slate-300">{user.email}</p>
                    </div>
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                        user.isActive
                          ? "bg-emerald-500/15 text-emerald-300"
                          : "bg-rose-500/15 text-rose-300"
                      }`}
                    >
                      {user.isActive ? "Active" : "Suspended"}
                    </span>
                  </div>
                  <div className="mt-3 flex items-center justify-between text-sm text-slate-400">
                    <span className="capitalize">{user.role || "user"}</span>
                    <span>{new Date(user.createdAt).toLocaleDateString()}</span>
                  </div>
                  <button
                    onClick={() => toggleUser(user._id)}
                    disabled={Boolean(togglingUserId)}
                    aria-busy={togglingUserId === user._id}
                    className={`mt-4 w-full rounded-xl px-3 py-2 text-sm font-semibold transition ${
                      user.isActive
                        ? "bg-rose-500/15 text-rose-300 hover:bg-rose-500/25"
                        : "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"
                    }`}
                  >
                    {togglingUserId === user._id ? "Updating…" : user.isActive ? "Suspend User" : "Activate User"}
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
